import { useState } from "react";
import "@/App.css";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Zap, 
  Brain, 
  Users, 
  TrendingUp, 
  Palette, 
  Settings,
  Target,
  MessageSquare,
  PenTool,
  Heart,
  Search,
  Globe,
  Share2,
  Image,
  ShoppingCart,
  BarChart3,
  Sparkles,
  Clock,
  DollarSign,
  CheckCircle2,
  ChevronRight,
  Rocket,
  Star,
  Play,
  Mail,
  Volume2,
  VolumeX
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Toaster } from "@/components/ui/sonner";

const CTA_LINK = "https://playosinc.pxf.io/qWDMKO";
const VIDEO_URL = "https://customer-assets.emergentagent.com/job_anvoa-sintra-team/artifacts/siv23hat_299_Video_IdeaFeatureSwipingFootage_SintraX_MartinB_Var2%20%281%29.mp4";

// Robot Face SVG Component - Fun expressions for each agent
const RobotFace = ({ expression, color, bgColor }) => {
  const faces = {
    happy: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Eyes */}
        <ellipse cx="35" cy="40" rx="8" ry="10" fill="white" />
        <ellipse cx="65" cy="40" rx="8" ry="10" fill="white" />
        <circle cx="35" cy="42" r="5" fill="#1a1a2e" />
        <circle cx="65" cy="42" r="5" fill="#1a1a2e" />
        <circle cx="37" cy="40" r="2" fill="white" />
        <circle cx="67" cy="40" r="2" fill="white" />
        {/* Smile */}
        <path d="M 30 60 Q 50 80 70 60" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    cool: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Sunglasses */}
        <rect x="22" y="35" width="22" height="14" rx="3" fill="#1a1a2e" />
        <rect x="56" y="35" width="22" height="14" rx="3" fill="#1a1a2e" />
        <line x1="44" y1="42" x2="56" y2="42" stroke="#1a1a2e" strokeWidth="3" />
        <line x1="78" y1="38" x2="90" y2="32" stroke="#1a1a2e" strokeWidth="3" />
        <line x1="10" y1="32" x2="22" y2="38" stroke="#1a1a2e" strokeWidth="3" />
        {/* Smirk */}
        <path d="M 35 65 Q 50 72 70 62" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    wink: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Left eye (winking) */}
        <path d="M 25 42 Q 35 38 45 42" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Right eye */}
        <ellipse cx="65" cy="40" rx="8" ry="10" fill="white" />
        <circle cx="65" cy="42" r="5" fill="#1a1a2e" />
        <circle cx="67" cy="40" r="2" fill="white" />
        {/* Smile */}
        <path d="M 30 60 Q 50 78 70 60" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    surprised: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Big eyes */}
        <ellipse cx="35" cy="40" rx="10" ry="12" fill="white" />
        <ellipse cx="65" cy="40" rx="10" ry="12" fill="white" />
        <circle cx="35" cy="42" r="6" fill="#1a1a2e" />
        <circle cx="65" cy="42" r="6" fill="#1a1a2e" />
        <circle cx="37" cy="39" r="2" fill="white" />
        <circle cx="67" cy="39" r="2" fill="white" />
        {/* O mouth */}
        <ellipse cx="50" cy="68" rx="8" ry="10" fill="white" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    thinking: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Eyes looking up */}
        <ellipse cx="35" cy="40" rx="8" ry="10" fill="white" />
        <ellipse cx="65" cy="40" rx="8" ry="10" fill="white" />
        <circle cx="37" cy="37" r="5" fill="#1a1a2e" />
        <circle cx="67" cy="37" r="5" fill="#1a1a2e" />
        {/* Thinking mouth */}
        <path d="M 40 65 L 60 65" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Thought bubble */}
        <circle cx="80" cy="25" r="4" fill="white" opacity="0.8" />
        <circle cx="88" cy="18" r="3" fill="white" opacity="0.6" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    excited: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Star eyes */}
        <text x="28" y="48" fontSize="20" fill="white">★</text>
        <text x="58" y="48" fontSize="20" fill="white">★</text>
        {/* Big smile */}
        <path d="M 25 58 Q 50 85 75 58" stroke="white" strokeWidth="4" fill="white" opacity="0.9" />
        {/* Antenna bouncing */}
        <line x1="50" y1="5" x2="45" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="45" cy="5" r="4" fill={color} />
        <line x1="50" y1="5" x2="55" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="55" cy="5" r="4" fill={color} />
      </svg>
    ),
    smart: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Glasses */}
        <circle cx="35" cy="40" r="12" fill="none" stroke="white" strokeWidth="3" />
        <circle cx="65" cy="40" r="12" fill="none" stroke="white" strokeWidth="3" />
        <line x1="47" y1="40" x2="53" y2="40" stroke="white" strokeWidth="3" />
        {/* Eyes behind glasses */}
        <circle cx="35" cy="40" r="4" fill="#1a1a2e" />
        <circle cx="65" cy="40" r="4" fill="#1a1a2e" />
        {/* Slight smile */}
        <path d="M 38 62 Q 50 70 62 62" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    friendly: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Happy closed eyes */}
        <path d="M 25 40 Q 35 35 45 40" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        <path d="M 55 40 Q 65 35 75 40" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Blush */}
        <ellipse cx="25" cy="55" rx="8" ry="5" fill={color} opacity="0.4" />
        <ellipse cx="75" cy="55" rx="8" ry="5" fill={color} opacity="0.4" />
        {/* Big smile */}
        <path d="M 30 58 Q 50 78 70 58" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    determined: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Determined eyes */}
        <ellipse cx="35" cy="42" rx="8" ry="8" fill="white" />
        <ellipse cx="65" cy="42" rx="8" ry="8" fill="white" />
        <circle cx="35" cy="42" r="5" fill="#1a1a2e" />
        <circle cx="65" cy="42" r="5" fill="#1a1a2e" />
        {/* Eyebrows */}
        <line x1="25" y1="30" x2="45" y2="33" stroke="white" strokeWidth="3" strokeLinecap="round" />
        <line x1="75" y1="30" x2="55" y2="33" stroke="white" strokeWidth="3" strokeLinecap="round" />
        {/* Confident smile */}
        <path d="M 35 62 Q 50 70 65 62" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    creative: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Artsy eyes - different sizes */}
        <ellipse cx="35" cy="40" rx="10" ry="8" fill="white" />
        <ellipse cx="65" cy="42" rx="7" ry="9" fill="white" />
        <circle cx="35" cy="40" r="5" fill="#1a1a2e" />
        <circle cx="65" cy="42" r="4" fill="#1a1a2e" />
        {/* Paint splatter */}
        <circle cx="80" cy="30" r="5" fill={color} opacity="0.6" />
        <circle cx="20" cy="65" r="4" fill={color} opacity="0.5" />
        {/* Quirky smile */}
        <path d="M 35 60 Q 45 72 60 65 Q 70 60 72 55" stroke="white" strokeWidth="3" fill="none" strokeLinecap="round" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    ),
    helpful: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Friendly eyes */}
        <ellipse cx="35" cy="40" rx="9" ry="10" fill="white" />
        <ellipse cx="65" cy="40" rx="9" ry="10" fill="white" />
        <circle cx="36" cy="42" r="5" fill="#1a1a2e" />
        <circle cx="66" cy="42" r="5" fill="#1a1a2e" />
        <circle cx="38" cy="40" r="2" fill="white" />
        <circle cx="68" cy="40" r="2" fill="white" />
        {/* Heart */}
        <text x="70" y="25" fontSize="14" fill={color}>♥</text>
        {/* Warm smile */}
        <path d="M 32 58 Q 50 75 68 58" stroke="white" strokeWidth="4" fill="none" strokeLinecap="round" />
        {/* Antenna with heart */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <text x="44" y="8" fontSize="12" fill={color}>♥</text>
      </svg>
    ),
    analytical: (
      <svg viewBox="0 0 100 100" className="w-full h-full">
        <circle cx="50" cy="50" r="45" fill={bgColor} />
        <circle cx="50" cy="50" r="40" fill={color} opacity="0.15" />
        {/* Scanner eyes */}
        <rect x="25" y="35" width="20" height="12" rx="2" fill="white" />
        <rect x="55" y="35" width="20" height="12" rx="2" fill="white" />
        <line x1="28" y1="41" x2="42" y2="41" stroke="#1a1a2e" strokeWidth="2" />
        <line x1="58" y1="41" x2="72" y2="41" stroke="#1a1a2e" strokeWidth="2" />
        {/* Data display mouth */}
        <rect x="35" y="58" width="30" height="15" rx="3" fill="white" opacity="0.9" />
        <line x1="40" y1="63" x2="60" y2="63" stroke={color} strokeWidth="2" />
        <line x1="40" y1="68" x2="55" y2="68" stroke={color} strokeWidth="2" />
        {/* Antenna */}
        <line x1="50" y1="5" x2="50" y2="15" stroke={color} strokeWidth="3" />
        <circle cx="50" cy="5" r="4" fill={color} />
      </svg>
    )
  };
  
  return faces[expression] || faces.happy;
};

// 12 AI Employees Data with expressions
const employees = {
  growth: [
    {
      name: "Buddy",
      role: "Business Strategy",
      description: "Your strategic partner for market analysis and growth opportunities.",
      icon: Target,
      color: "#10b981",
      bgColor: "#d1fae5",
      expression: "smart"
    },
    {
      name: "Milli",
      role: "Sales Manager",
      description: "Closes deals and converts leads into loyal customers with precision.",
      icon: TrendingUp,
      color: "#10b981",
      bgColor: "#d1fae5",
      expression: "determined"
    },
    {
      name: "Seomi",
      role: "SEO Specialist",
      description: "Dominates search rankings and drives organic visibility.",
      icon: Globe,
      color: "#10b981",
      bgColor: "#d1fae5",
      expression: "analytical"
    },
    {
      name: "Scouty",
      role: "Recruiting",
      description: "Finds and attracts top talent to build your dream team.",
      icon: Search,
      color: "#10b981",
      bgColor: "#d1fae5",
      expression: "thinking"
    }
  ],
  creative: [
    {
      name: "Penn",
      role: "Copywriting",
      description: "Crafts compelling copy that converts and captivates.",
      icon: PenTool,
      color: "#f43f5e",
      bgColor: "#ffe4e6",
      expression: "creative"
    },
    {
      name: "Emmie",
      role: "Email Marketing",
      description: "Creates email campaigns that engage and drive action.",
      icon: Mail,
      color: "#f43f5e",
      bgColor: "#ffe4e6",
      expression: "friendly"
    },
    {
      name: "Soshie",
      role: "Social Media",
      description: "Builds your social presence with viral, shareable content.",
      icon: Share2,
      color: "#f43f5e",
      bgColor: "#ffe4e6",
      expression: "excited"
    },
    {
      name: "Vizzy",
      role: "Virtual Assistant",
      description: "Creates stunning visuals and manages creative workflows.",
      icon: Image,
      color: "#f43f5e",
      bgColor: "#ffe4e6",
      expression: "cool"
    },
    {
      name: "Commet",
      role: "eCommerce",
      description: "Optimizes your store for maximum conversions.",
      icon: ShoppingCart,
      color: "#f43f5e",
      bgColor: "#ffe4e6",
      expression: "wink"
    }
  ],
  operations: [
    {
      name: "Cassie",
      role: "Customer Support",
      description: "Delivers exceptional 24/7 support that keeps customers happy.",
      icon: MessageSquare,
      color: "#f59e0b",
      bgColor: "#fef3c7",
      expression: "helpful"
    },
    {
      name: "Dexter",
      role: "Data Analyst",
      description: "Transforms raw data into actionable business insights.",
      icon: BarChart3,
      color: "#f59e0b",
      bgColor: "#fef3c7",
      expression: "analytical"
    },
    {
      name: "Gigi",
      role: "Personal Coach",
      description: "Guides your team with personalized coaching and advice.",
      icon: Heart,
      color: "#f59e0b",
      bgColor: "#fef3c7",
      expression: "friendly"
    }
  ]
};

// All 12 agents for hero showcase with expressions
const allAgents = [
  { name: "Milli", role: "Sales Manager", color: "#ec4899", bgColor: "#fce7f3", expression: "determined" },
  { name: "Soshie", role: "Social Media", color: "#a855f7", bgColor: "#f3e8ff", expression: "excited" },
  { name: "Cassie", role: "Customer Support", color: "#3b82f6", bgColor: "#dbeafe", expression: "helpful" },
  { name: "Buddy", role: "Business Strategy", color: "#6366f1", bgColor: "#e0e7ff", expression: "smart" },
  { name: "Scouty", role: "Recruiting", color: "#14b8a6", bgColor: "#ccfbf1", expression: "thinking" },
  { name: "Penn", role: "Copywriting", color: "#84cc16", bgColor: "#ecfccb", expression: "creative" },
  { name: "Seomi", role: "SEO Specialist", color: "#22c55e", bgColor: "#dcfce7", expression: "analytical" },
  { name: "Gigi", role: "Personal Coach", color: "#06b6d4", bgColor: "#cffafe", expression: "friendly" },
  { name: "Vizzy", role: "Virtual Assistant", color: "#eab308", bgColor: "#fef9c3", expression: "cool" },
  { name: "Emmie", role: "Email Marketing", color: "#f59e0b", bgColor: "#ffedd5", expression: "friendly" },
  { name: "Dexter", role: "Data Analyst", color: "#ef4444", bgColor: "#fee2e2", expression: "analytical" },
  { name: "Commet", role: "eCommerce", color: "#f97316", bgColor: "#fed7aa", expression: "wink" }
];

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.08
    }
  }
};

// Components
const Navbar = () => (
  <motion.nav 
    initial={{ opacity: 0, y: -20 }}
    animate={{ opacity: 1, y: 0 }}
    className="fixed top-0 left-0 right-0 z-50 px-6 py-4 bg-white/90 backdrop-blur-lg border-b border-gray-100"
  >
    <div className="max-w-7xl mx-auto flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold text-indigo-600">ANVOA</span>
        <span className="text-gray-400">×</span>
        <span className="text-2xl font-bold text-cyan-500">SINTRA</span>
      </div>
      <a 
        href={CTA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2.5 rounded-full font-bold text-sm hover:shadow-lg hover:shadow-orange-500/30 transition-all hover:scale-105"
        data-testid="nav-cta-btn"
      >
        Get Started <ArrowRight size={16} className="inline ml-1" />
      </a>
    </div>
  </motion.nav>
);

// Image URLs from user's assets
const SOSHIE_IMAGE = "https://customer-assets.emergentagent.com/job_anvoa-sintra-team/artifacts/lhebx4x8_254_Image_SoshieSocialMedia_SintraX_MartinB_Var1.jpeg";
const TEAM_IMAGE = "https://customer-assets.emergentagent.com/job_anvoa-sintra-team/artifacts/bbhofhb3_274_Image_WaitlistRecreations_SintraX_MartinB_Var6%20%281%29.jpeg";

const VideoHero = () => {
  const [isMuted, setIsMuted] = useState(true);
  
  return (
    <section className="min-h-screen pt-24 pb-12 bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 relative overflow-hidden" data-testid="hero-section">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-purple-500/20 rounded-full blur-[100px]" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/20 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-pink-500/10 rounded-full blur-[150px]" />
      </div>
      
      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center min-h-[calc(100vh-120px)]">
          
          {/* LEFT SIDE - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8"
          >
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/20">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <span className="text-white font-semibold">12 AI agents for less than $1/day</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight">
              <span className="text-white">Your </span>
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">AI Workforce</span>
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-pink-500 to-purple-400">Awaits</span>
            </h1>
            
            {/* Subheadline */}
            <p className="text-xl text-gray-300 max-w-xl leading-relaxed">
              12 AI employees. 3 departments. One mission:{" "}
              <span className="text-white font-semibold">Make your business unstoppable.</span>
            </p>
            
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4 pt-4">
              <a 
                href={CTA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="group bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-orange-500/40 transition-all hover:scale-105 flex items-center gap-3"
                data-testid="hero-cta-primary"
              >
                <Rocket size={22} />
                Start Building Your Team
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <a 
                href={CTA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white/10 backdrop-blur-md text-white px-8 py-4 rounded-full font-bold text-lg border border-white/30 hover:bg-white/20 transition-all flex items-center gap-3"
                data-testid="hero-cta-secondary"
              >
                <Play size={18} />
                See It In Action
              </a>
            </div>
            
            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-6 pt-4 text-white/70">
              <div className="flex items-center gap-2">
                <div className="flex -space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={18} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="font-bold text-white">4.5/5</span>
                <span className="text-sm">Trustpilot</span>
              </div>
              <div className="hidden sm:block h-5 w-px bg-white/30" />
              <div className="flex items-center gap-2">
                <CheckCircle2 size={18} className="text-green-400" />
                <span>7,800+ Reviews</span>
              </div>
            </div>
          </motion.div>
          
          {/* RIGHT SIDE - Video */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative lg:scale-110 lg:origin-left"
          >
            {/* Video Container with sleek frame */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl shadow-purple-500/30 border border-white/10 bg-black/20 backdrop-blur-sm">
              {/* Glowing border effect */}
              <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-cyan-500/20 via-purple-500/20 to-pink-500/20 blur-sm" />
              
              <div className="relative rounded-3xl overflow-hidden">
                <video
                  autoPlay
                  loop
                  muted={isMuted}
                  playsInline
                  className="w-full aspect-[16/10] object-cover min-h-[400px] lg:min-h-[480px]"
                  data-testid="hero-video"
                >
                  <source src={VIDEO_URL} type="video/mp4" />
                </video>
                
                {/* Video overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                
                {/* Mute button on video */}
                <button
                  onClick={() => setIsMuted(!isMuted)}
                  className="absolute bottom-4 right-4 bg-white/20 backdrop-blur-md p-2.5 rounded-full hover:bg-white/30 transition-all"
                  data-testid="mute-toggle"
                >
                  {isMuted ? <VolumeX size={20} className="text-white" /> : <Volume2 size={20} className="text-white" />}
                </button>
              </div>
            </div>
            
            {/* CTA Button below video */}
            <div className="mt-6 text-center">
              <a 
                href={CTA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-gray-900 px-8 py-4 rounded-full font-bold text-lg hover:shadow-xl hover:shadow-white/20 transition-all hover:scale-105"
                data-testid="video-cta"
              >
                Try Now, Before Prices Go Up <ArrowRight size={18} />
              </a>
            </div>
            
            {/* Floating badge */}
            <motion.div 
              className="absolute -top-4 -right-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              🚀 Just Launched
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      {/* Scroll indicator */}
      <motion.div 
        className="absolute bottom-6 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 8, 0] }}
        transition={{ duration: 2, repeat: Infinity }}
      >
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex justify-center pt-2">
          <div className="w-1.5 h-3 bg-white/60 rounded-full" />
        </div>
      </motion.div>
    </section>
  );
};

const RobotAvatar = ({ agent, index }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay: index * 0.05, type: "spring", stiffness: 200 }}
    className="relative group"
  >
    <div 
      className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg cursor-pointer transition-all duration-300 group-hover:scale-110 group-hover:rotate-6 group-hover:shadow-xl"
      style={{ background: agent.bgColor }}
    >
      <RobotFace expression={agent.expression} color={agent.color} bgColor={agent.bgColor} />
    </div>
    <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap bg-gray-900 text-white text-xs px-3 py-1.5 rounded-lg font-semibold z-10">
      {agent.name}
      <div className="text-gray-400 text-[10px]">{agent.role}</div>
    </div>
  </motion.div>
);

const MeetSquadSection = () => (
  <section className="py-20 lg:py-28 px-6 lg:px-12 bg-gradient-to-b from-gray-50 to-white" data-testid="squad-section">
    <div className="max-w-6xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center"
      >
        <Badge className="bg-gradient-to-r from-purple-100 to-pink-100 text-purple-600 border-purple-200 mb-6">
          Meet Your New Team
        </Badge>
        <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900">
          Meet Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-cyan-500">AI Squad</span>
        </h2>
        <p className="text-xl text-gray-600 mb-12 max-w-2xl mx-auto">
          12 specialists, 3 departments, infinite possibilities
        </p>
        
        {/* Sintra Team Image */}
        <div className="mb-12">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl max-w-4xl mx-auto border border-gray-100">
            <img 
              src={TEAM_IMAGE} 
              alt="AI Employees - Your AI Workforce" 
              className="w-full h-auto object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <p className="text-white text-xl font-bold">AI Employees just launched.</p>
              <p className="text-purple-300 text-lg">Delegate your tasks now.</p>
            </div>
          </div>
        </div>
        
        {/* Robot Face Grid */}
        <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100 inline-block">
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-6 justify-items-center">
            {allAgents.map((agent, i) => (
              <RobotAvatar key={agent.name} agent={agent} index={i} />
            ))}
          </div>
          <div className="mt-10 pt-6 border-t border-gray-100 flex justify-center gap-4 flex-wrap">
            <Badge className="badge-growth px-4 py-2">🎯 Growth</Badge>
            <Badge className="badge-creative px-4 py-2">🎨 Creative</Badge>
            <Badge className="badge-operations px-4 py-2">⚙️ Operations</Badge>
          </div>
        </div>
        
        <div className="mt-10">
          <a 
            href={CTA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-full font-bold text-lg hover:shadow-lg hover:shadow-indigo-500/30 transition-all hover:scale-105"
            data-testid="squad-cta"
          >
            Hire Your Squad <ArrowRight size={20} />
          </a>
        </div>
      </motion.div>
    </div>
  </section>
);

const ValueProposition = () => (
  <section className="py-20 lg:py-28 px-6 lg:px-12" data-testid="value-section">
    <div className="max-w-7xl mx-auto">
      <motion.div 
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="text-center mb-16"
      >
        <Badge className="bg-green-100 text-green-600 border-green-200 mb-6">
          Why AI Workforce?
        </Badge>
        <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900">
          Work <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-500">Smarter</span>, 
          Not Harder
        </h2>
        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
          Traditional tools are boring. Your AI Workforce is anything but.
        </p>
      </motion.div>
      
      {/* Featured Spotlight - Soshie */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="mb-16"
      >
        <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-800 rounded-3xl overflow-hidden shadow-2xl">
          <div className="grid md:grid-cols-2 gap-0">
            <div className="p-10 lg:p-14 flex flex-col justify-center">
              <Badge className="bg-white/20 text-white border-white/30 mb-4 w-fit">Featured Employee</Badge>
              <h3 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Your social media manager, <span className="text-purple-200">on AI.</span>
              </h3>
              <p className="text-purple-200 text-lg mb-6">
                Meet Soshie - your 24/7 social media powerhouse. Creating viral content while you sleep.
              </p>
              <a 
                href={CTA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-white text-purple-700 px-6 py-3 rounded-full font-bold hover:shadow-lg transition-all w-fit"
              >
                Hire Soshie <ArrowRight size={18} />
              </a>
            </div>
            <div className="relative">
              <img 
                src={SOSHIE_IMAGE} 
                alt="Soshie - AI Social Media Manager"
                className="w-full h-full object-cover min-h-[300px]"
              />
            </div>
          </div>
        </div>
      </motion.div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          {
            icon: Clock,
            title: "Always On Duty",
            description: "24/7 availability across all time zones. No coffee breaks needed.",
            color: "#6366f1",
            bgColor: "#eef2ff"
          },
          {
            icon: Zap,
            title: "Instant Superpowers",
            description: "No training required. Each AI is ready to deliver results immediately.",
            color: "#06b6d4",
            bgColor: "#ecfeff"
          },
          {
            icon: DollarSign,
            title: "Budget Friendly",
            description: "Get an entire department's output at a fraction of the cost.",
            color: "#10b981",
            bgColor: "#d1fae5"
          },
          {
            icon: Brain,
            title: "Gets Smarter Daily",
            description: "AI that learns your style, preferences, and keeps improving.",
            color: "#8b5cf6",
            bgColor: "#ede9fe"
          },
          {
            icon: Rocket,
            title: "Scale in Seconds",
            description: "Need more help? Add another AI employee instantly.",
            color: "#f43f5e",
            bgColor: "#ffe4e6"
          },
          {
            icon: Users,
            title: "Team Players",
            description: "Our AIs collaborate seamlessly, like a well-oiled machine.",
            color: "#f59e0b",
            bgColor: "#fef3c7"
          }
        ].map((item, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
            className="value-card group"
          >
            <div 
              className="feature-icon"
              style={{ background: item.bgColor }}
            >
              <item.icon style={{ color: item.color }} size={28} />
            </div>
            <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
            <p className="text-gray-600 leading-relaxed">{item.description}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const EmployeeCard = ({ employee, dept }) => {
  const Icon = employee.icon;
  const deptStyles = {
    growth: "badge-growth",
    creative: "badge-creative", 
    operations: "badge-operations"
  };
  
  const deptLabels = {
    growth: "Growth",
    creative: "Creative",
    operations: "Operations"
  };
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="agent-card"
    >
      {/* Robot Avatar Header */}
      <div 
        className="h-40 flex items-center justify-center relative overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${employee.bgColor} 0%, white 100%)` }}
      >
        <div className="w-24 h-24">
          <RobotFace expression={employee.expression} color={employee.color} bgColor={employee.bgColor} />
        </div>
      </div>
      
      <div className="p-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className={`text-xs font-bold px-3 py-1 rounded-full ${deptStyles[dept]}`}>
            {deptLabels[dept]}
          </span>
          <Icon size={20} style={{ color: employee.color }} />
        </div>
        
        <div>
          <h3 className="text-xl font-bold text-gray-900">{employee.name}</h3>
          <p className="text-sm font-semibold" style={{ color: employee.color }}>{employee.role}</p>
        </div>
        
        <p className="text-gray-600 text-sm leading-relaxed">{employee.description}</p>
        
        <a 
          href={CTA_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-2 font-bold text-sm transition-colors pt-2 hover:underline"
          style={{ color: employee.color }}
          data-testid={`hire-${employee.name.toLowerCase()}-btn`}
        >
          Hire {employee.name} <ChevronRight size={16} />
        </a>
      </div>
    </motion.div>
  );
};

const EmployeeCatalog = () => {
  const [activeTab, setActiveTab] = useState("growth");
  
  return (
    <section className="py-20 lg:py-28 px-6 lg:px-12 bg-gray-50" data-testid="catalog-section">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <Badge className="bg-orange-100 text-orange-600 border-orange-200 mb-6">
            Full Roster
          </Badge>
          <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900">
            12 AI Employees.{" "}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 via-orange-500 to-yellow-500">
              3 Departments.
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Each specialist is powered by Sintra's advanced AI technology. 
            Hire individuals or assemble your complete workforce.
          </p>
        </motion.div>
        
        <Tabs defaultValue="growth" value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="flex justify-center gap-2 mb-12 bg-transparent flex-wrap">
            <TabsTrigger 
              value="growth"
              className={`tab-btn ${activeTab === 'growth' ? 'active' : ''}`}
              data-testid="tab-growth"
            >
              <TrendingUp size={18} className="mr-2" />
              Growth (4)
            </TabsTrigger>
            <TabsTrigger 
              value="creative"
              className={`tab-btn ${activeTab === 'creative' ? 'active' : ''}`}
              data-testid="tab-creative"
            >
              <Palette size={18} className="mr-2" />
              Creative (5)
            </TabsTrigger>
            <TabsTrigger 
              value="operations"
              className={`tab-btn ${activeTab === 'operations' ? 'active' : ''}`}
              data-testid="tab-operations"
            >
              <Settings size={18} className="mr-2" />
              Operations (3)
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="growth">
            <div className="agent-grid">
              {employees.growth.map((emp, i) => (
                <EmployeeCard key={i} employee={emp} dept="growth" />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="creative">
            <div className="agent-grid">
              {employees.creative.map((emp, i) => (
                <EmployeeCard key={i} employee={emp} dept="creative" />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="operations">
            <div className="agent-grid max-w-4xl mx-auto">
              {employees.operations.map((emp, i) => (
                <EmployeeCard key={i} employee={emp} dept="operations" />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

const FinalCTA = () => (
  <section className="py-20 lg:py-28 px-6 lg:px-12" data-testid="final-cta-section">
    <motion.div 
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="max-w-4xl mx-auto text-center bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 rounded-[40px] p-12 lg:p-20 relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-20 -right-20 w-60 h-60 bg-white/10 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-60 h-60 bg-white/10 rounded-full blur-3xl" />
      </div>
      
      <div className="relative z-10">
        <h2 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white">
          Ready to Build Your
          <br />
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">Dream Team?</span>
        </h2>
        <p className="text-xl text-white/90 mb-10 max-w-2xl mx-auto">
          Join 50,000+ businesses powered by their AI Workforce. 
          Your first AI employee is one click away.
        </p>
        <div className="flex flex-wrap justify-center gap-4 mb-10">
          <a 
            href={CTA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white text-indigo-600 px-10 py-5 rounded-full font-bold text-lg hover:shadow-2xl hover:shadow-white/30 transition-all hover:scale-105 flex items-center gap-3"
            data-testid="final-cta-btn"
          >
            <Rocket size={24} /> Get Started Now
          </a>
          <a 
            href={CTA_LINK}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-white/10 backdrop-blur-md text-white px-8 py-5 rounded-full font-bold text-lg border border-white/30 hover:bg-white/20 transition-all flex items-center gap-3"
            data-testid="final-explore-btn"
          >
            <Users size={20} /> View All 12 Employees
          </a>
        </div>
        
        <div className="flex flex-wrap justify-center gap-6 text-white/80">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-green-300" size={18} />
            <span>12 Specialized AI Employees</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-green-300" size={18} />
            <span>3 Complete Departments</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="text-green-300" size={18} />
            <span>Powered by Sintra AI</span>
          </div>
        </div>
      </div>
    </motion.div>
  </section>
);

const Footer = () => (
  <footer className="py-10 px-6 bg-white border-t border-gray-100">
    <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
      <div className="flex items-center gap-2">
        <span className="text-xl font-bold text-indigo-600">ANVOA</span>
        <span className="text-gray-400">×</span>
        <span className="text-xl font-bold text-cyan-500">SINTRA</span>
      </div>
      <p className="text-gray-500 text-sm">
        © {new Date().getFullYear()} Anvoa. The Complete AI Workforce.
      </p>
      <a 
        href={CTA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        className="text-orange-500 hover:text-orange-600 transition-colors text-sm font-bold"
        data-testid="footer-cta-link"
      >
        Get Started →
      </a>
    </div>
  </footer>
);

function App() {
  return (
    <div className="App min-h-screen bg-white">
      <Toaster position="top-right" />
      <Navbar />
      <main>
        <VideoHero />
        <MeetSquadSection />
        <ValueProposition />
        <EmployeeCatalog />
        <FinalCTA />
      </main>
      <Footer />
      
      {/* Emergent Badge - Fixed Bottom Right */}
      <a
        href="https://app.emergent.sh/?via=anvoa"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-4 right-4 z-50 bg-black text-white px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 hover:bg-gray-800 transition-all shadow-lg"
        data-testid="emergent-badge"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Built with Emergent
      </a>
    </div>
  );
}

export default App;
